import { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { HomeModeContext } from '../../contexts/HomeModeContext';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import {
  ArrowRight,
  Lightning,
  Stack,
  Upload,
  ChartLineUp,
  Fingerprint,
  SlidersHorizontal,
  BookOpenText,
  ArrowSquareOut,
  Sun,
  Moon,
  CaretDown,
  Cursor,
} from '@phosphor-icons/react';
import { ContextMatchingAnimation } from '../ui/ContextMatchingAnimation';
import CellRevealLogo, { CellRevealIcon } from '../ui/CellRevealLogo';

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.15 }
  }
};


const ROTATING_CONTEXTS = [
  'Brain Tissue',
  'Lung Cancer',
  'Blood Samples',
  'Liver Disease',
  'Tumor Microenvironments',
  'Heart Tissue',
  'Kidney Disease',
  'Breast Cancer',
  'Immune Infiltrates',
  'Bone Marrow',
  'Skin Biopsies',
  'Pancreatic Tissue',
];

const publications = [
  {
    journal: 'Genome Biology',
    year: '2026',
    title: 'omnideconv: a unifying framework for using and benchmarking single-cell-informed deconvolution of bulk RNA-seq data',
    authors: 'A. Dietrich, L. Merotto, et al., M. List, F. Finotello',
    href: 'https://doi.org/10.1186/s13059-026-03955-w',
  },
  {
    journal: 'Bioinformatics',
    year: '2022',
    title: 'SimBu: bias-aware simulation of bulk RNA-seq data with variable cell-type composition',
    authors: 'A. Dietrich, F. Finotello, et al. M. List',
    href: 'https://doi.org/10.1093/bioinformatics/btac499',
  },
  {
    journal: 'Bioinformatics',
    year: '2019',
    title: 'Comprehensive evaluation of transcriptome-based cell-type quantification methods for immuno-oncology',
    authors: 'G. Sturm, F. Finotello, et al., M. List, et al.',
    href: 'https://doi.org/10.1093/bioinformatics/btz363',
  },
  {
    journal: 'Nature Reviews Genetics',
    year: '2019',
    title: 'Next-generation computational tools for interrogating cancer immunity',
    authors: 'F. Finotello, et al.',
    href: 'https://doi.org/10.1038/s41576-019-0166-7',
  },
];

export function Home() {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isSpinning, setIsSpinning] = useState(true);
  const { isDark, setIsDark } = useContext(HomeModeContext);
  const N = ROTATING_CONTEXTS.length;
  const d = isDark;

  useEffect(() => {
    if (isSpinning) {
      let spinCount = 0;
      const totalSpins = N * 2 + Math.floor(Math.random() * 4);
      let delay = 50;
      const spinStep = () => {
        setScrollPosition((prev) => prev + 1);
        spinCount++;
        if (spinCount >= totalSpins) { setIsSpinning(false); return; }
        if (spinCount > totalSpins - 6) delay += 60;
        setTimeout(spinStep, delay);
      };
      const t = setTimeout(spinStep, 300);
      return () => clearTimeout(t);
    } else {
      const interval = setInterval(() => {
        setScrollPosition((prev) => prev + 1);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [isSpinning, N]);

  const steps = [
    {
      step: '01',
      title: 'Upload your data',
      desc: 'Drag and drop your gene expression matrix or select from previously published datasets. We accept any import format.',
      icon: Upload
    },
    {
      step: '02',
      title: 'Select a signature',
      desc: 'Browse our library by tissue type, disease context, and organism.',
      icon: Stack
    },
    {
      step: '03',
      title: 'Run the analysis',
      desc: 'Cloud infrastructure processes your data using fast state-of-the-art deconvolution algorithms.',
      icon: Lightning
    },
    {
      step: '04',
      title: 'Explore your results',
      desc: 'Interactive bar charts, heatmaps, and per-sample proportions. Download CSV, JSON, or a formatted PDF report.',
      icon: ChartLineUp
    }
  ];

  const tissueNames = ['Brain', 'Heart', 'Blood', 'Tumor', 'Lung', 'Liver', 'Kidney', 'Bone Marrow', 'Skin', '...and more'];

  return (
    <div className={`min-h-screen pt-16 transition-colors duration-300 ${d ? 'bg-navy-950' : 'bg-background'}`}>

      {/* ── Mode toggle — fixed floating pill ─────────────────────────────── */}
      <motion.button
        onClick={() => setIsDark(!isDark)}
        whileTap={{ scale: 0.92 }}
        whileHover={{ scale: 1.04 }}
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
          d
            ? 'bg-navy-900 border border-teal-700/40 text-teal-300 hover:border-teal-500/60 hover:text-teal-100'
            : 'bg-white border border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-700 shadow-md'
        }`}
        aria-label={d ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={d ? 'sun' : 'moon'}
            initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            style={{ display: 'flex' }}
          >
            {d ? <Sun className="w-4 h-4" weight="bold" /> : <Moon className="w-4 h-4" weight="bold" />}
          </motion.span>
        </AnimatePresence>
        <span>{d ? 'Light' : 'Dark'}</span>
      </motion.button>

      {/* ── Hero + Bridge — continuous opening act ─────────────────────────── */}
      <div className={`relative transition-colors duration-300 ${d ? 'bg-navy-950' : 'bg-white'}`}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: d
              ? 'linear-gradient(to right, rgb(94 234 212) 1px, transparent 1px), linear-gradient(to bottom, rgb(94 234 212) 1px, transparent 1px)'
              : 'linear-gradient(to right, rgb(20 184 166) 1px, transparent 1px), linear-gradient(to bottom, rgb(20 184 166) 1px, transparent 1px)',
            backgroundSize: '72px 72px',
            opacity: d ? 0.04 : 0.055,
            transition: 'opacity 0.3s',
          }}
        />
        {/* Grain texture — depth in dark mode, very subtle in light */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E\")",
            opacity: d ? 0.028 : 0.012,
            transition: 'opacity 0.3s',
          }}
        />
        {/* Light mode: subtle ambient gradient */}
        {!d && (
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-teal-50/30 via-transparent to-teal-50/15" />
        )}

        {/* ── Hero ──────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden min-h-[90dvh] flex items-center">
          <div className={`absolute right-[-4%] top-1/2 -translate-y-1/2 w-[580px] h-[580px] rounded-full blur-[100px] pointer-events-none transition-colors duration-300 ${d ? 'bg-teal-500/10' : 'bg-teal-400/6'}`} />
          <div className="absolute right-[-8%] sm:right-[-2%] top-1/2 -translate-y-1/2 pointer-events-none" style={{ opacity: d ? 0.22 : 0.12 }}>
            <CellRevealIcon
              variant={d ? 'dark' : 'light'}
              size="2xl"
              orbit
              pulseGlow
              style={{ width: 520, height: 520 }}
              ariaLabel=""
            />
          </div>
          <div className="relative w-full max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24">
            <motion.div
              className="max-w-[38rem] text-left"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.h1
                className="mb-6 leading-[1.08]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.8 }}
              >
                <span className="block text-4xl md:text-5xl lg:text-[3.4rem]" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
                  <span style={{ fontWeight: 700, letterSpacing: '-0.042em', color: d ? '#fef3c7' : '#134e4a' }}>Reveal</span>
                  {' '}
                  <span style={{ fontWeight: 300, letterSpacing: '-0.022em', color: d ? 'rgb(204 251 241)' : '#0d9488' }}>Cellular</span>
                </span>
                <span className={`block text-4xl md:text-5xl lg:text-[3.4rem] font-normal tracking-tight transition-colors duration-300 ${d ? 'text-white/80' : 'text-slate-600'}`}>
                  Composition in
                </span>
                <span className="block mt-1 text-4xl md:text-5xl lg:text-[3.4rem] font-bold" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", letterSpacing: '-0.042em' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      position: 'relative',
                      height: '2em',
                      overflow: 'hidden',
                      WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 28%, black 72%, transparent 100%)',
                      maskImage: 'linear-gradient(to bottom, transparent 0%, black 28%, black 72%, transparent 100%)',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="whitespace-nowrap pointer-events-none select-none"
                      style={{ visibility: 'hidden', display: 'block' }}
                    >
                      {ROTATING_CONTEXTS.reduce((a, b) => a.length > b.length ? a : b)}
                    </span>
                    <AnimatePresence initial={false}>
                      {([-1, 0, 1] as const).map((offset) => {
                        const slot = scrollPosition + offset;
                        const displayIndex = ((slot % N) + N) % N;
                        return (
                          <motion.span
                            key={slot}
                            className="whitespace-nowrap"
                            style={{ position: 'absolute', top: '0.46em', left: 0, color: d ? '#fef3c7' : '#134e4a' }}
                            initial={{ y: `${(offset + 1) * 1.08}em`, opacity: 0 }}
                            animate={{
                              y: `${(slot - scrollPosition) * 1.08}em`,
                              opacity: offset === 0 ? 1 : 0.4,
                            }}
                            exit={{ y: `${(offset - 1) * 1.08}em`, opacity: 0 }}
                            transition={{ duration: isSpinning ? 0.07 : 0.5, ease: [0.16, 1, 0.3, 1] }}
                          >
                            {ROTATING_CONTEXTS[displayIndex]}
                          </motion.span>
                        );
                      })}
                    </AnimatePresence>
                  </span>
                </span>
              </motion.h1>

              <motion.p
                className={`text-lg md:text-xl mb-10 leading-relaxed max-w-lg transition-colors duration-300 ${d ? 'text-teal-100/80' : 'text-muted-foreground'}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.7 }}
              >
                Quality-controlled cell-type signatures matched to your tissue, disease, and cohort: so bulk deconvolution is accurate from the start. No manual curation needed anymore.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-3 justify-start"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.7 }}
              >
                <a
                  href="mailto:hello@cellreveal.com"
                  className={`inline-flex items-center text-base px-8 py-[14px] font-semibold rounded-lg active:scale-[0.98] transition-all ${
                    d
                      ? 'bg-white text-teal-900 hover:bg-teal-50 shadow-[0_8px_32px_-8px_rgba(20,184,166,0.45)]'
                      : 'bg-teal-600 text-white hover:bg-teal-700 shadow-[0_8px_32px_-8px_rgba(20,184,166,0.35)]'
                  }`}
                >
                  Request early access
                  <ArrowRight className="ml-2 w-4 h-4" />
                </a>
                <button
                  onClick={() => document.getElementById('explainer')?.scrollIntoView({ behavior: 'smooth' })}
                  className={`inline-flex items-center text-base px-8 py-[14px] rounded-lg active:scale-[0.98] transition-colors ${
                    d
                      ? 'border border-teal-400/35 text-teal-100 bg-teal-500/10 hover:bg-teal-500/18 hover:text-white'
                      : 'border border-teal-300 text-teal-700 bg-teal-50 hover:bg-teal-100 hover:text-teal-800'
                  }`}
                >
                  See how it works
                </button>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </div>{/* end hero+bridge */}


      {/* ── Context Matching Animation ────────────────────────────────────── */}
      <div id="explainer" />
      <ContextMatchingAnimation isDark={isDark} />

      {/* ── Bridge: animation → how it works ─────────────────────────────── */}
      <div className={`relative flex flex-col items-center gap-3 py-10 transition-colors duration-300 ${
        d ? 'bg-navy-950' : 'bg-gradient-to-b from-[rgb(8_12_22)] to-slate-50'
      }`}>
        <p className={`text-[11px] font-mono uppercase tracking-[0.32em] transition-colors duration-300 ${
          d ? 'text-teal-400/35' : 'text-teal-400/55'
        }`}>
          So how does CellReveal solve this?
        </p>
        <button
          onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
          className={`group flex items-center gap-2 text-sm font-medium transition-colors duration-200 ${
            d ? 'text-teal-300/55 hover:text-teal-300' : 'text-teal-300/70 hover:text-teal-400'
          }`}
        >
          See the four-step workflow
          <motion.span
            animate={{ y: [0, 4, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
            className="flex"
          >
            <CaretDown className="w-4 h-4" />
          </motion.span>
        </button>
      </div>

      {/* ── How It Works ──────────────────────────────────────────────────── */}
      <section
        id="how-it-works"
        className={`py-24 transition-colors duration-300 ${d ? 'bg-[#0d1929] border-t border-teal-900/25' : 'bg-slate-50 border-t border-border'}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-16 grid md:grid-cols-[1fr_2fr] gap-10 items-end"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
          >
            <div>
              <span className={`font-mono text-[10px] tracking-[0.35em] uppercase block mb-4 transition-colors duration-300 ${d ? 'text-teal-400/55' : 'text-muted-foreground'}`}>
                How it works
              </span>
              <h2 className={`text-3xl md:text-4xl font-bold leading-[1.04] tracking-tight transition-colors duration-300 ${d ? 'text-teal-50' : 'text-foreground'}`}>
                Four steps.<br />Start to result.
              </h2>
            </div>
            <p className={`text-lg leading-relaxed pb-0.5 max-w-lg transition-colors duration-300 ${d ? 'text-teal-300/70' : 'text-muted-foreground'}`}>
              Upload your data, pick a context-matched signature, and get robust proportions in minutes.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            {steps.map((item, idx) => (
              <motion.div key={idx} variants={fadeInUp} className={`group rounded-xl p-4 -m-4 transition-colors duration-200 ${d ? 'hover:bg-white/[0.03]' : 'hover:bg-teal-50/60'}`}>
                <div className="mb-5">
                  <span className={`font-mono text-[2.25rem] font-extralight leading-none block tabular-nums transition-colors duration-300 ${d ? 'text-teal-400/30' : 'text-primary/22'}`}>
                    {item.step}
                  </span>
                  <div className={`mt-3 w-9 h-9 rounded-xl flex items-center justify-center transition-colors duration-300 ${d ? 'bg-teal-900/30 border border-teal-700/30' : 'bg-teal-50 border border-teal-100'}`}>
                    <item.icon className={`w-4 h-4 transition-colors duration-300 ${d ? 'text-teal-400' : 'text-teal-600'}`} />
                  </div>
                </div>
                <h4 className={`font-semibold text-sm mb-2 transition-colors duration-300 ${d ? 'text-teal-100 group-hover:text-teal-300' : 'text-foreground group-hover:text-primary'}`}>
                  {item.title}
                </h4>
                <p className={`text-sm leading-relaxed transition-colors duration-300 ${d ? 'text-teal-300/65' : 'text-muted-foreground'}`}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Try demo CTA */}
          <motion.div
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeInUp}
          >
            <Link
              to="/demo"
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-medium text-sm transition-all duration-200 active:scale-[0.98] ${
                d
                  ? 'bg-teal-500/12 border border-teal-500/25 text-teal-300 hover:bg-teal-500/20 hover:border-teal-400/40'
                  : 'bg-teal-600 text-white hover:bg-teal-700 shadow-[0_6px_20px_-6px_rgba(20,184,166,0.4)]'
              }`}
            >
              <Cursor className="w-4 h-4" weight="bold" />
              Try the interactive demo
              <ArrowRight className="w-3.5 h-3.5" weight="bold" />
            </Link>
            <p className={`text-sm transition-colors duration-300 ${d ? 'text-teal-400/45' : 'text-muted-foreground/60'}`}>
              Click through the full four-step workflow — no login required.
            </p>
          </motion.div>

        </div>
      </section>


      {/* ── Key Benefits ──────────────────────────────────────────────────── */}
      <section className={`py-24 transition-colors duration-300 ${d ? 'bg-[#0d1929] border-t border-teal-900/25' : 'bg-slate-50 border-t border-border'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          <motion.div
            className="mb-14 grid md:grid-cols-[1fr_2fr] gap-10 items-end"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            <div>
              <span className={`font-mono text-[10px] tracking-[0.35em] uppercase block mb-4 transition-colors duration-300 ${d ? 'text-teal-400/55' : 'text-muted-foreground'}`}>
                CellReveal benefits
              </span>
              <h2 className={`text-4xl md:text-5xl font-bold tracking-tight leading-[1.04] transition-colors duration-300 ${d ? 'text-teal-50' : 'text-foreground'}`}>
                Results in<br />minutes.
              </h2>
            </div>
            <p className={`text-lg leading-relaxed pb-0.5 max-w-lg transition-colors duration-300 ${d ? 'text-teal-300/70' : 'text-muted-foreground'}`}>
              Context-matched, validated signatures, streamlined deconvolution, and no environment to configure. So the biology remains center stage.
            </p>
          </motion.div>

          <motion.div
            className={`grid grid-cols-1 md:grid-cols-3 gap-px transition-colors duration-300 ${d ? 'bg-teal-900/20' : 'bg-teal-200/40'}`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={staggerContainer}
          >
            {/* Speed — featured 2-col */}
            <motion.div variants={fadeInUp} className={`md:col-span-2 p-10 md:p-12 transition-colors duration-300 ${d ? 'bg-navy-950' : 'bg-background'}`}>
              <span className={`font-mono text-[10px] tracking-[0.35em] uppercase block mb-7 transition-colors duration-300 ${d ? 'text-teal-400/55' : 'text-muted-foreground'}`}>
                Time to result
              </span>
              <div className="flex items-end gap-4 mb-6">
                <span className={`text-5xl md:text-6xl font-bold font-display leading-none tracking-tight transition-colors duration-300 ${d ? 'text-teal-400' : 'text-teal-600'}`}>
                  minutes
                </span>
                <span className={`text-sm font-mono pb-1 line-through decoration-2 transition-colors duration-300 ${d ? 'text-red-400/65 decoration-red-400' : 'text-red-500/60 decoration-red-500'}`}>
                  weeks
                </span>
              </div>
              <p className={`text-base leading-relaxed max-w-sm transition-colors duration-300 ${d ? 'text-teal-300/65' : 'text-muted-foreground'}`}>
                Upload your expression matrix, pick a reference, and receive cell-type proportions. Without writing a single line of code, we have all the tools you need.
              </p>
            </motion.div>

            {/* Versioned signatures — 1-col */}
            <motion.div variants={fadeInUp} className={`p-10 transition-colors duration-300 ${d ? 'bg-navy-950' : 'bg-background'}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-7 transition-colors duration-300 ${d ? 'bg-teal-900/30 border border-teal-700/30' : 'bg-teal-50 border border-teal-200'}`}>
                <Fingerprint className={`w-4 h-4 transition-colors duration-300 ${d ? 'text-teal-400' : 'text-teal-600'}`} />
              </div>
              <h4 className={`text-base font-semibold mb-3 leading-snug transition-colors duration-300 ${d ? 'text-teal-100' : 'text-foreground'}`}>
                Citable, versioned signatures
              </h4>
              <p className={`text-sm leading-relaxed transition-colors duration-300 ${d ? 'text-teal-300/65' : 'text-muted-foreground'}`}>
                Every reference is version-controlled. Cite the exact signature used, so that reviewers can verify it, collaborators can reproduce it.
              </p>
            </motion.div>

            {/* Algorithm choice — 1-col */}
            <motion.div variants={fadeInUp} className={`p-10 transition-colors duration-300 ${d ? 'bg-navy-950' : 'bg-background'}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-7 transition-colors duration-300 ${d ? 'bg-amber-900/20 border border-amber-700/25' : 'bg-amber-50 border border-amber-200'}`}>
                <SlidersHorizontal className={`w-4 h-4 transition-colors duration-300 ${d ? 'text-amber-400' : 'text-amber-700'}`} />
              </div>
              <h4 className={`text-base font-semibold mb-3 leading-snug transition-colors duration-300 ${d ? 'text-teal-100' : 'text-foreground'}`}>
                Streamlined deconvolution
              </h4>
              <p className={`text-sm leading-relaxed transition-colors duration-300 ${d ? 'text-teal-300/65' : 'text-muted-foreground'}`}>
                Validated deconvolution workflows, prepared by experts in the field. You simply choose the biological context and we do the rest.
              </p>
            </motion.div>

            {/* Coverage — featured 2-col */}
            <motion.div variants={fadeInUp} className={`md:col-span-2 p-10 md:p-12 transition-colors duration-300 ${d ? 'bg-navy-950' : 'bg-background'}`}>
              <span className={`font-mono text-[10px] tracking-[0.35em] uppercase block mb-7 transition-colors duration-300 ${d ? 'text-teal-400/55' : 'text-muted-foreground'}`}>
                Context coverage
              </span>
              <div className="flex flex-wrap gap-2 mb-6">
                {tissueNames.map((t) => (
                  <span key={t} className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors duration-300 ${d ? 'border-teal-700/40 text-teal-300 bg-teal-800/20' : 'border-teal-200/70 text-teal-700 bg-teal-50/60'}`}>
                    {t}
                  </span>
                ))}
              </div>
              <p className={`text-base leading-relaxed max-w-sm transition-colors duration-300 ${d ? 'text-teal-300/65' : 'text-muted-foreground'}`}>
                A blood signature will not perform robustly on tumor samples. Expression profiles between disease and normal tissue vary significantly. Our library is organized by tissue, disease, and organism, so the right reference is one click away, not three weeks of literature review.
              </p>
            </motion.div>
          </motion.div>

        </div>
      </section>


      {/* ── Publications ──────────────────────────────────────────────────── */}
      <section className={`py-24 transition-colors duration-300 ${d ? 'bg-navy-950 border-t border-teal-900/25' : 'bg-background border-t border-border'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-[1fr_2fr] gap-12 md:gap-20 items-start">

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={fadeInUp}
            >
              <span className={`font-mono text-[10px] tracking-[0.35em] uppercase block mb-4 transition-colors duration-300 ${d ? 'text-teal-400/55' : 'text-muted-foreground'}`}>
                The research behind it
              </span>
              <h2 className={`text-3xl md:text-4xl font-bold leading-[1.04] tracking-tight mb-5 transition-colors duration-300 ${d ? 'text-teal-50' : 'text-foreground'}`}>
                Built on peer-reviewed science.
              </h2>
              <p className={`leading-relaxed text-base transition-colors duration-300 ${d ? 'text-teal-300/65' : 'text-muted-foreground'}`}>
                CellReveal evolved from our latest research on cell-type deconvolution. With our{' '}
                <a href="https://omnideconv.org/" className={`underline transition-colors duration-200 ${d ? 'text-teal-400 hover:text-teal-300' : 'text-teal-600 hover:text-teal-700'}`}>omnideconv</a>{' '}
                ecosystem, we benchmarked modern deconvolution algorithms and could pinpoint context-mismatch as the key failure mode in deconvolution applications. Our combined expertise in deconvolution spans 20+ years of research and applications.
              </p>
            </motion.div>

            <motion.div
              className={`divide-y transition-colors duration-300 ${d ? 'divide-teal-900/30' : 'divide-border/50'}`}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
              variants={staggerContainer}
            >
              {publications.map((pub, i) => (
                <motion.div
                  key={i}
                  variants={fadeInUp}
                  className="py-7 first:pt-0 flex items-start justify-between gap-6 group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2.5">
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${d ? 'bg-teal-900/25 border border-teal-700/25' : 'bg-teal-50 border border-teal-100'}`}>
                        <BookOpenText className={`w-3.5 h-3.5 transition-colors duration-300 ${d ? 'text-teal-400' : 'text-teal-600'}`} />
                      </div>
                      <span className={`font-mono text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${d ? 'text-teal-400' : 'text-teal-600'}`}>{pub.journal}</span>
                      <span className={`text-xs font-mono transition-colors duration-300 ${d ? 'text-teal-400/40' : 'text-muted-foreground/35'}`}>{pub.year}</span>
                    </div>
                    <h4 className={`text-base font-semibold mb-1.5 leading-snug transition-colors duration-300 ${d ? 'text-teal-100 group-hover:text-teal-300' : 'text-foreground group-hover:text-teal-700'}`}>
                      {pub.title}
                    </h4>
                    <p className={`text-sm transition-colors duration-300 ${d ? 'text-teal-300/60' : 'text-muted-foreground'}`}>{pub.authors}</p>
                  </div>
                  <a
                    href={pub.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 hover:scale-110 ${
                      d
                        ? 'border border-teal-800/40 hover:border-teal-500 text-teal-400/40 hover:text-teal-400'
                        : 'border border-border hover:border-teal-400 hover:text-teal-600 text-muted-foreground/35'
                    }`}
                    aria-label="Open publication"
                  >
                    <ArrowSquareOut className="w-4 h-4" />
                  </a>
                </motion.div>
              ))}
            </motion.div>

          </div>
        </div>
      </section>


      {/* ── FAQ → CTA ─────────────────────────────────────────────────────── */}
      <section className={`pt-24 pb-28 relative overflow-hidden transition-colors duration-300 ${d ? 'bg-navy-900 border-t border-teal-900/35' : 'bg-[#fef3c7]'}`}>
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] rounded-full"
            style={{ background: d ? 'radial-gradient(ellipse, rgba(20,184,166,0.11) 0%, transparent 70%)' : 'radial-gradient(ellipse, rgba(212,165,116,0.18) 0%, transparent 70%)' }}
          />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-[1fr_auto] gap-10 items-center"
          >
            <div>
              <motion.div variants={fadeInUp} className="mb-10">
                <span className={`font-mono text-[10px] tracking-[0.35em] uppercase block mb-3 transition-colors duration-300 ${d ? 'text-teal-400/40' : 'text-teal-800/45'}`}>FAQ</span>
                <p className={`text-base transition-colors duration-300 ${d ? 'text-teal-200/50' : 'text-navy-950/45'}`}>
                  No questions arise frequently — CellReveal is not yet in the hands of many researchers.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp}>
                <h2 className={`text-3xl md:text-4xl font-bold mb-4 leading-tight tracking-tight transition-colors duration-300 ${d ? 'text-white' : 'text-navy-950'}`}>
                  Be among the first<br className="hidden sm:block" /> to change that.
                </h2>
                <p className={`text-lg max-w-lg transition-colors duration-300 ${d ? 'text-teal-200/60' : 'text-navy-950/60'}`}>
                  CellReveal is built for pharmaceutical and biotech research teams that need reliable, reproducible cell-type deconvolution. We are currently onboarding early research partners.
                </p>
              </motion.div>
            </div>

            <motion.div variants={fadeInUp} className="flex flex-col gap-3 shrink-0">
              <a
                href="mailto:hello@cellreveal.com"
                className={`inline-flex items-center justify-center px-7 py-3.5 font-semibold rounded-lg active:scale-[0.98] transition-colors ${
                  d
                    ? 'bg-white text-teal-800 hover:bg-teal-50'
                    : 'bg-navy-950 text-cream-100 hover:bg-navy-900'
                }`}
              >
                Request early access
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className={`pt-16 pb-10 transition-colors duration-300 ${d ? 'bg-navy-950 border-t border-teal-900/25' : 'bg-slate-100 border-t border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-10 md:gap-16 mb-14">
            <div>
              <CellRevealLogo variant={d ? 'dark' : 'light'} size="md" />
              <p className={`mt-5 text-sm leading-relaxed max-w-xs transition-colors duration-300 ${d ? 'text-teal-300/55' : 'text-slate-500'}`}>
                Context-matched cell-type deconvolution for research teams.
              </p>
            </div>
            <div>
              <h5 className={`text-xs tracking-[0.2em] uppercase font-semibold mb-5 transition-colors duration-300 ${d ? 'text-white/50' : 'text-slate-500'}`}>Contact</h5>
              <div className="flex flex-col gap-3">
                <a
                  href="mailto:hello@cellreveal.com"
                  className={`text-sm transition-colors ${d ? 'text-teal-200/60 hover:text-teal-100' : 'text-slate-600 hover:text-teal-600'}`}
                >
                  hello@cellreveal.com
                </a>
                {[
                  { label: 'Privacy policy', to: '#' },
                  { label: 'Terms of service', to: '#' },
                  { label: 'Impressum', to: '/impressum' },
                ].map(link => (
                  <Link
                    key={link.label}
                    to={link.to}
                    className={`text-sm transition-colors ${d ? 'text-teal-200/60 hover:text-teal-100' : 'text-slate-600 hover:text-teal-600'}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <div className={`border-t pt-7 flex flex-col sm:flex-row justify-between items-center gap-4 transition-colors duration-300 ${d ? 'border-teal-900/25' : 'border-slate-200'}`}>
            <p className={`text-xs transition-colors duration-300 ${d ? 'text-teal-300/40' : 'text-slate-400'}`}>
              © {new Date().getFullYear()} CellReveal. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
}
